import { useEffect, useRef } from "react";
import maplibregl, { FullscreenControl, LngLat } from "maplibre-gl";
import { renderToStaticMarkup } from "react-dom/server";
import "maplibre-gl/dist/maplibre-gl.css";

import { useMapContext } from "../context/MapContext";
import { useModal } from "../context/ModalContext";
import { selectIcon } from "../utils";

import { SERVICE_COLORS } from "../constants";

export default function MapView() {
  const { setMapInstance, setUserCoords, markers, filteredMarkers, setMarkers } = useMapContext();

  const { showModal } = useModal();

  const mapRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (mapRef.current) return;
    mapRef.current = new maplibregl.Map({
      container: "map",
      style: "/mapStyle.json",
      center: [-65, -40],
      zoom: 3.5,
    });

    setMapInstance(mapRef.current);

    const geolocate = new maplibregl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      showAccuracyCircle: false,
    });

    geolocate.on("geolocate", ({ coords }) => {
      const userCoords = [coords.longitude, coords.latitude];
      setUserCoords(userCoords);
      updateMarkerDistances(userCoords);
    });

    mapRef.current.addControl(geolocate);
    mapRef.current.addControl(new FullscreenControl({ container: document.body }));

    return () => mapRef.current?.remove();
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    filteredMarkers.forEach((marker) => {
      const el = createMarkerElement(marker);
      el.onclick = () => showModal(marker);

      const instance = new maplibregl.Marker({ element: el }).setLngLat(marker.coords).addTo(mapRef.current);

      markersRef.current.push(instance);
    });
  }, [filteredMarkers, showModal]);

  function updateMarkerDistances(userCoords) {
    const userLngLat = LngLat.convert(userCoords);
    setMarkers((prev) =>
      prev.map((marker) => {
        try {
          return {
            ...marker,
            distance: userLngLat.distanceTo(LngLat.convert(marker.coords)),
          };
        } catch {
          return { ...marker, distance: -1 };
        }
      }),
    );
  }

  return <div id="map" className="h-full w-full" />;
}

function createMarkerElement(marker) {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = renderToStaticMarkup(selectIcon(marker.category));
  const svg = wrapper.firstChild;

  svg.classList.add("cursor-pointer");

  const types = marker.serviceType;

  svg.querySelectorAll('[fill="currentColor"]').forEach((path) => {
    path.setAttribute("stroke", "white");
    path.setAttribute("stroke-width", "1");
  });
  if (types.length === 1) {
    svg.querySelectorAll('[fill="currentColor"]').forEach((path) => {
      path.setAttribute("fill", SERVICE_COLORS[types[0]]);
    });
  } else if (types.length === 2) {
    const gradientId = `marker-gradient-${marker.id}`;
    const color1 = SERVICE_COLORS[types[0]];
    const color2 = SERVICE_COLORS[types[1]];

    const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    const linearGradient = document.createElementNS("http://www.w3.org/2000/svg", "linearGradient");
    linearGradient.setAttribute("id", gradientId);
    linearGradient.setAttribute("x1", "0");
    linearGradient.setAttribute("y1", "0");
    linearGradient.setAttribute("x2", "1");
    linearGradient.setAttribute("y2", "1");

    [
      [0, color1],
      [50, color1],
      [50, color2],
      [100, color2],
    ].forEach(([offset, stopColor]) => {
      const stop = document.createElementNS("http://www.w3.org/2000/svg", "stop");
      stop.setAttribute("offset", `${offset}%`);
      stop.setAttribute("stop-color", stopColor);
      linearGradient.appendChild(stop);
    });

    defs.appendChild(linearGradient);
    svg.prepend(defs);

    svg.querySelectorAll('[fill="currentColor"]').forEach((path) => {
      path.setAttribute("fill", `url(#${gradientId})`);
    });
  } else {
    svg.querySelectorAll('[fill="currentColor"]').forEach((path) => {
      path.setAttribute("fill", SERVICE_COLORS.ALL);
    });
  }

  return svg;
}
